import { useRef } from "react";

export interface WaveformBarsProps {
  levels: number[]; // 0-1 per bar
  progress?: number; // 0-1 played fraction; omit for live-recording mode (no "played" split)
  onSeek?: (fraction: number) => void; // click/drag-to-seek, playback mode only
  className?: string;
}

// Plain DOM bars, not canvas - this app has zero canvas usage anywhere else
// (every other visual is CSS-driven), 48 nodes is trivial to paint, and
// click/drag-to-seek is a free pointer handler instead of manual canvas hit
// testing. Bars pick up the light/dark theme automatically via the app's
// existing CSS custom properties, same as every other chat element.
export function WaveformBars({ levels, progress, onSeek, className }: WaveformBarsProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const playedCount = progress !== undefined ? Math.round(progress * levels.length) : -1;

  function seekAt(clientX: number): void {
    if (!onSeek || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const fraction = rect.width > 0 ? Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)) : 0;
    onSeek(fraction);
  }

  return (
    <div
      ref={containerRef}
      className={`chat-waveform${className ? ` ${className}` : ""}${onSeek ? " is-seekable" : ""}`}
      onPointerDown={onSeek ? (e) => seekAt(e.clientX) : undefined}
      role={onSeek ? "slider" : undefined}
      aria-valuemin={onSeek ? 0 : undefined}
      aria-valuemax={onSeek ? 100 : undefined}
      aria-valuenow={onSeek && progress !== undefined ? Math.round(progress * 100) : undefined}
      aria-label={onSeek ? "Seek" : undefined}
    >
      {levels.map((level, i) => (
        <span
          key={i}
          className={`chat-waveform-bar${playedCount >= 0 && i < playedCount ? " is-played" : ""}`}
          style={{ height: `${Math.max(8, level * 100)}%` }}
        />
      ))}
    </div>
  );
}
